import { Country, ImageFile } from "app/shared/shared.types";

export const categories = [
    'users',
    'releases',
    'tracks',
    /* 'artists', */
    'stores',
    /* 'types', */
    'labels',
    'countries',
] as const;
export type AnalyticsCategoryType = typeof categories[number];

export const insightsColumns = [
    'device',
    'end_reason',
    'source',
    'offline',
    'paying',
    'subscription',
    'first_time_listen',
    'os',
    'age_group',
    'play_percent_group',
    'gender',
    'completed',
] as const;
export type AnalyticsInsightsColumn = typeof insightsColumns[number];

//Not necessarily the same as categories
export const filters = [
    'users',
    'releases',
    'tracks',
    'artists',
    'labels',
    'stores',
    'types',
    'countries',
] as const;
export type AnalyticsFilterType = typeof filters[number];

export interface PartnerStatsDTO {
    "current_users": number;
    "previous_users": number;
    "current_releases": number;
    "previous_releases": number;
    "current_artists": number;
    "previous_artists": number;
    "current_tracks": number;
    "previous_tracks": number;
    "current_subscriptions": number;
    "previous_subscriptions": number;
    "total_orders": number;
    "open_orders": number;
    current_stores: number;
}
export type PartnerStatsKey = keyof PartnerStatsDTO;

export interface AdminStatsDTO {
    "current_users": number;
    "previous_users": number;
    "current_partners": number;
    "previous_partners": number;
    "current_releases": number;
    "previous_releases": number;
    "current_tracks": number;
    "previous_tracks": number;
    "current_subscriptions": number;
    "previous_subscriptions": number;
    "current_subscription_revenue": number;
    "previous_subscription_revenue": number;
    "current_service_revenue": number;
    "previous_service_revenue": number;
    current_stores: number;
}
export type AdminStatsKey = keyof AdminStatsDTO;

export class AnalyticsFilterConfigDTO {
    users?: string[] = [];
    releases: string[] = [];
    tracks: string[] = [];
    artists: string[] = [];
    labels: string[] = [];
    stores: string[] = [];
    types: ('stream' | 'download')[] = [];
    countries: string[] = [];
}

export interface AnalyticsFilter {
    uuid: string;
    title?: string;
    logo?: ImageFile;
}

export interface NewFilterConfig {
    type: AnalyticsFilterType;
    value: AnalyticsFilter;
}

export interface SortConfig {
    sortBy: string;
    sortDirection: 'asc' | 'desc' | '';
}

export class AnalyticsFilterConfig {
    users?: AnalyticsFilter[];
    releases?: AnalyticsFilter[];
    tracks?: AnalyticsFilter[];
    artists?: AnalyticsFilter[];
    stores?: AnalyticsFilter[];
    types?: AnalyticsFilter[];
    labels?: AnalyticsFilter[];
    countries?: AnalyticsFilter[];

    add(filterType: AnalyticsFilterType, value: AnalyticsFilter) {
        if (!this[filterType]) {
            this[filterType] = [];
        }
        if (this[filterType].findIndex(existingValue => existingValue.uuid === value.uuid) === -1) {
            this[filterType] = this[filterType].concat(value);
        }
    }

    remove(filterType: AnalyticsFilterType, value: AnalyticsFilter) {
        this[filterType] = this[filterType].filter((existingValue) => existingValue.uuid !== value.uuid);
        if (this[filterType].length === 0) {
            this[filterType] = undefined;
        }
    }

    toDTO(): AnalyticsFilterConfigDTO {
        return {
            releases: this.releases?.map(val => val.uuid) ?? [],
            tracks: this.tracks?.map(val => val.uuid) ?? [],
            artists: this.artists?.map(val => val.uuid) ?? [],
            stores: this.stores?.map(val => val.uuid) ?? [],
            labels: this.labels?.map(val => val.uuid) ?? [],
            types: this.types?.map(val => val.uuid as unknown as ("stream" | "download")) ?? [],
            countries: this.countries?.map(val => val.uuid) ?? [],
            users: this.users?.map(val => val.uuid),
        }
    }
}

export class AnalyticsRequestDTO {
    startDate: Date;
    endDate: Date;
    maxRows?: number;
    filter?: AnalyticsFilterConfigDTO;
    includeSocials?: boolean;
}

export class AnalyticsInsightsRequestDTO extends AnalyticsRequestDTO {
    baseColumn: AnalyticsInsightsColumn;
    detailColumn: AnalyticsInsightsColumn;
}

export class ReportRequestDTO {
    reportUuid?: string;
    reportType: string;
    maxRows: number;
    currency?: string;
    comparison?: string;
    filter?: AnalyticsFilterConfigDTO;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    startDate?: Date;
    endDate?: Date;
}

export class AnalyticsFilterRequestDTO {
    filter?: AnalyticsFilterConfigDTO;
    search?: string;
}

export interface AnalyticsDataDTO {
    data: AnalyticsDataRow[];
    totals?: AnalyticsDataRowTotals;
}

export interface AnalyticsInsightsResponseDTO {
    storeName: string;
    data: AnalyticsInsightsRowDTO[];
    baseColumn: AnalyticsInsightsColumn;
    detailColumn: AnalyticsInsightsColumn;
}

export interface AnalyticsInsightsRowDTO {
    base_col: number;
    detail_col: number;
    revenue: number;
    streams: number;
}

export interface TimestampedData {
    //The names are defined this way by ApexCharts
    x: string;
    y: number;
}

interface AnalyticsDataRowBase {
    metadata?: AnalyticsRowMetadata
}

export interface AnalyticsRowMetadata {
    uuid?: string;
    title?: string;
    subTitle?: string;
    artwork?: ImageFile;
    logo?: ImageFile;
    avatar?: ImageFile;
    country?: Country;
    video?: {
        title: string;
        videoId: string;
        channelName: string;
        tnUrl: string;
    }
}

export interface AnalyticsDataRow extends AnalyticsDataRowBase {
    stats?: {
        labels: string[];
        revenue: number[];
        streams: number[];
        premium_streams?: number[];
        artificials?: number[];
        artificialsPercentage?: number[];
        creations: number[];
        pps?: number[];
        likes?: number[];
        comments?: number[];
        shares?: number[];
        favorites?: number[];
        avg_duration?: number[];
        downloads: number[];
        percentages?: number[];
    },
    comparison?: AnalyticsDataRowTotals,
    totals?: AnalyticsDataRowTotals,
    timestampedStats?: {
        revenuePerDay: TimestampedData[],
        artificialsPerDay?: TimestampedData[],
        artificialPercentagesPerDay?: TimestampedData[],
        streamsPerDay: TimestampedData[],
        downloadsPerDay: TimestampedData[]
        creationsPerDay: TimestampedData[]
    }
}

export interface AnalyticsDemographicsEntry {
    entities: AnalyticsRowMetadata[];
    series: number[];
}

export interface AnalyticsDemographicsDTO {
    releases: AnalyticsDemographicsEntry;
    stores: AnalyticsDemographicsEntry;
    artists: AnalyticsDemographicsEntry;
    countries: AnalyticsDemographicsEntry;
}

export interface AnalyticsDataRowTotals {
    revenue: number;
    streams: number;
    downloads: number;
    creations: number;
    pps: number;
    likes?: number;
    comments?: number;
    shares?: number;
    favorites?: number;
    avg_duration?: number;
    premium_streams?: number;
}

export type TotalsData = { name: string, data: TimestampedData[] }

export interface MetricsRowData {
    value: number,
    change?: number,
    subText?: string;
    subTextValue?: string;
    currency?: boolean;
}

export interface MetricsRows {
    users: MetricsRowData,
    releases: MetricsRowData,
    artists?: MetricsRowData;
    partners?: MetricsRowData;
    tracks: MetricsRowData;
    stores?: MetricsRowData;
    subscriptions: MetricsRowData;
    subscriptionRevenue?: MetricsRowData;
    serviceRevenue?: MetricsRowData;
    orders?: MetricsRowData;
    revenue: MetricsRowData;
}
